// M21 Benutzer: Liste mit Rolle/Status/MFA, Einladen (Link, solange kein SMTP), Rolle ändern, sperren, entfernen.
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { can, qk, useMe, useUsers } from '../api/queries';
import type { TenantRole, TenantUser } from '../api/types';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Confirm, Dialog, Field, PageHead, relativeTime, Spinner } from '../components/ui';
import { localeOf } from '../i18n';
import { TenantMfaPolicyCard } from './account/SecurityPolicy';

const ROLES: TenantRole[] = ['TenantAdmin', 'Editor', 'Viewer'];

export function InviteLink({ token }: { token: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const link = `${window.location.origin}/invite/${token}`;
  return (
    <div className="col" style={{ gap: 6 }}>
      <div className="hint">{t('users.linkHint')}</div>
      <div className="row">
        <input className="grow mono" readOnly value={link} aria-label={t('users.inviteLink')} onFocus={(e) => e.target.select()} data-testid="invite-link" />
        <button className="btn" onClick={() => void navigator.clipboard?.writeText(link).then(() => toast.show(t('common.copied')))}><Icon name="copy" />{t('common.copy')}</button>
      </div>
    </div>
  );
}

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: users, isLoading } = useUsers();
  const qc = useQueryClient();
  const toast = useToast();
  const [invite, setInvite] = useState(false);
  const [remove, setRemove] = useState<TenantUser | null>(null);
  const [mfaReset, setMfaReset] = useState<TenantUser | null>(null);
  const manage = can(me, 'users.manage');

  const patch = async (u: TenantUser, body: { role?: TenantRole; status?: 'active' | 'disabled' }) => {
    try {
      await api.patch(`/users/${u.id}`, body, { ifMatch: u.rowVersion });
      await qc.invalidateQueries({ queryKey: qk.users });
      toast.show(t('common.saved'));
    } catch (e) {
      toast.error(e);
      await qc.invalidateQueries({ queryKey: qk.users });
    }
  };

  return (
    <>
      <PageHead title={t('nav.users')} sub={t('users.sub')}
        actions={manage && <button className="btn primary" onClick={() => setInvite(true)}><Icon name="plus" />{t('users.invite')}</button>} />
      {isLoading ? <Spinner /> : (
        <div className="card scroll" style={{ padding: 10 }}>
          <table className="list">
            <thead><tr><th>{t('common.name')}</th><th>{t('users.role')}</th><th>{t('users.status')}</th><th>MFA</th><th>{t('users.lastLogin')}</th><th /></tr></thead>
            <tbody>
              {(users ?? []).map((u) => {
                const self = u.id === me?.user.id;
                return (
                  <tr key={u.id}>
                    <td><b>{u.displayName}</b>{self && <span className="muted small"> ({t('users.you')})</span>}<div className="muted small">{u.email}</div></td>
                    <td>
                      {manage && !self ? (
                        <select aria-label={t('users.role')} value={u.role} onChange={(e) => void patch(u, { role: e.target.value as TenantRole })} style={{ width: 'auto' }}>
                          {ROLES.map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
                        </select>
                      ) : t(`roles.${u.role}`)}
                    </td>
                    <td><span className={`pill ${u.status === 'active' ? 'ok' : u.status === 'invited' ? 'info' : 'error'}`}>{t(`users.st.${u.status}`)}</span></td>
                    <td>{u.mfaActive ? <span className="pill ok plain">{t('common.yes')}</span> : <span className="muted">{t('common.no')}</span>}</td>
                    <td className="muted">{u.lastLoginAt ? relativeTime(u.lastLoginAt, locale) : '–'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {manage && !self && (u.status === 'disabled'
                        ? <button className="btn sm ghost" onClick={() => void patch(u, { status: 'active' })}>{t('users.enable')}</button>
                        : <button className="btn sm ghost" onClick={() => void patch(u, { status: 'disabled' })}>{t('users.disable')}</button>)}
                      {manage && !self && u.mfaActive && <button className="btn sm ghost" onClick={() => setMfaReset(u)}>{t('account.reset2fa.action')}</button>}
                      {manage && !self && <button className="btn sm ghost icon danger" aria-label={t('common.remove')} title={t('common.remove')} onClick={() => setRemove(u)}><Icon name="trash" /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="hint" style={{ marginTop: 14 }}>{t('users.rolesHint')}</div>
      {can(me, 'tenant.settings.manage') && <TenantMfaPolicyCard />}
      {mfaReset && (
        <Confirm danger title={t('account.reset2fa.title')} text={t('account.reset2fa.text', { name: mfaReset.displayName })} confirmLabel={t('account.reset2fa.action')} onClose={() => setMfaReset(null)}
          onConfirm={async () => {
            try {
              await api.post(`/users/${mfaReset.id}/mfa-reset`);
              await qc.invalidateQueries({ queryKey: qk.users });
              toast.show(t('account.reset2fa.done', { name: mfaReset.displayName }));
            } catch (e) {
              toast.error(e);
            }
            setMfaReset(null);
          }} />
      )}
      {invite && <InviteDialog onClose={() => setInvite(false)} />}
      {remove && (
        <Confirm danger title={t('common.remove')} text={t('users.confirmRemove', { name: remove.displayName })} confirmLabel={t('common.remove')} onClose={() => setRemove(null)}
          onConfirm={async () => {
            try {
              await api.del(`/users/${remove.id}`);
              await qc.invalidateQueries({ queryKey: qk.users });
            } catch (e) {
              toast.error(e);
            }
            setRemove(null);
          }} />
      )}
    </>
  );
}

function InviteDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TenantRole>('Editor');
  const [token, setToken] = useState<string | null>(null);
  const submit = async () => {
    try {
      const res = await api.post<{ token: string }>('/users/invitations', { email: email.trim(), role });
      setToken(res.token);
      await qc.invalidateQueries({ queryKey: qk.users });
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={t('users.invite')} onClose={onClose} actions={token ? <button className="btn primary" onClick={onClose}>{t('common.close')}</button> : <>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!email.includes('@')} onClick={submit}>{t('users.sendInvite')}</button>
    </>}>
      {token ? <InviteLink token={token} /> : (
        <form className="col" onSubmit={(e) => { e.preventDefault(); if (email.includes('@')) void submit(); }}>
          <Field label={t('login.email')}><input type="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <Field label={t('users.role')}>
            <select value={role} onChange={(e) => setRole(e.target.value as TenantRole)}>{ROLES.map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}</select>
          </Field>
          <div className="hint">{t(`roles.${role}Text`)}</div>
        </form>
      )}
    </Dialog>
  );
}
